import * as React from 'react';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import AppBar from '@mui/material/AppBar';
import Container from '@mui/material/Container';
import Toolbar from '@mui/material/Toolbar';
import Paper from '@mui/material/Paper';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Favicon from 'react-favicon';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Alert from '@mui/material/Alert';
import { appTheme } from '../themes/theme';
import useFetchGet from '../api/useFetchGet';

const icon = require('../assets/images/Q-logos_black.png');
const logo = require('../assets/images/logo.png');

function Copyright() {
  return (
    <Typography variant='body2' color='text.secondary' align='center'>
            Copyright ©
      <Link color='inherit' href='https://qashio.com/'>
        {' '}QASHIO{' '}
      </Link>
      {new Date().getFullYear()}
    </Typography>
  );
}

const theme = createTheme(appTheme);

export default function ResetPassword() {
  useEffect(() => {
    document.title = 'User Signup';
  });

  const { search } = useLocation();
  const resetPassword = new URLSearchParams(search).get('reset_password_token');
  const validResetPasswordToken = useFetchGet(`${process.env.REACT_APP_SERVER_HOSTNAME}/valid_reset_password_token?token=${resetPassword}`);
  const [openError, setOpenError] = React.useState(false);
  const [openSuccess, setOpenSuccess] = React.useState(false);
  const [openResetPassword, setOpenResetPassword] = React.useState(true);
  const [userInfo, setUserInfo] = React.useState('');
  
  const [errors, setErrors] = React.useState(
    {
      password: '',
      password_confirmation: '',
    },
  );

  const [userData, setUserData] = React.useState(
    {
      password: '',
      password_confirmation: '',
    },
  );

  const handleChange = (name: string, value: string) => {
    setErrors({ ...errors, password: '', password_confirmation: '' });
    setUserData({ ...userData, [name]: value });
  };

  const handleNext = () => {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/;

    if (userData.password === '') {
      setErrors({ ...errors, password: 'This field required' });
    } else if (!passwordRegex.test(userData.password)) {
      setErrors({ 
        ...errors, 
        password: 'Password must be 8 chars with 1 uppercase, 1 lowercase, 1 number & 1 special char'
      });
    } else if (userData.password_confirmation === '') {
      setErrors({ ...errors, password_confirmation: 'This field required' });
    } else if (userData.password !== userData.password_confirmation) {
      setErrors({ ...errors, password_confirmation: 'confirm password not match password' });
    } else {
      fetch(`${process.env.REACT_APP_SERVER_HOSTNAME}/reset_password?reset_password_token=${resetPassword}`, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        method: 'PUT',
        body: JSON.stringify(userData),
      })
        .then(async (response) => {
          await response.json();
          if (response.status === 200) {
            setUserInfo('Password Successfully Changed');
            setOpenResetPassword(false);
            setOpenSuccess(true);
            setOpenError(false);
          } else {
            setUserInfo('Token Expired');
            setOpenError(true);
            setOpenSuccess(false);
          }
        })
        .catch(() => {
          setUserInfo('Oops Something Went Wrong');
          setOpenError(true);
          setOpenSuccess(false);
        });
    }
  };
    
  const getComponent = () => {
    if (validResetPasswordToken.isPending === false) {
      if (validResetPasswordToken.data.error) {
        return (
          <Container component='main' maxWidth='sm' sx={{ mb: 4 }}>
            <Paper variant='outlined' sx={{ my: { xs: 3, md: 6 }, p: { xs: 2, md: 3 } }}>
              <Typography component='h1' variant='h4' align='center'>
                {validResetPasswordToken.data.error}
              </Typography>
            </Paper>
            <Copyright />
          </Container>
        );
      }
      return (
        <Container component='main' maxWidth='sm' sx={{ mb: 4 }}>
          <Paper variant='outlined' sx={{ my: { xs: 3, md: 6 }, p: { xs: 2, md: 3 } }}>
            <Typography component='h1' variant='h4' align='center'>
                            User Reset Password
            </Typography>
            <>
              <Stack sx={{ width: '100%', mt: 4 }} spacing={2}>
                <Collapse in={openError}>
                  <Alert severity='error'>
                    {userInfo}
                  </Alert>
                </Collapse>
                <Collapse in={openSuccess}>
                  <Alert severity='success'>
                    {userInfo}
                  </Alert>
                </Collapse>
              </Stack>
              <Collapse in={openResetPassword}>
                <Typography variant='h6' gutterBottom sx={{ mt: 2 }}>
                Reset Password
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      margin='normal'
                      required
                      fullWidth
                      name='password'
                      label='Password'
                      type='password'
                      id='password'
                      error={errors.password !== ''}
                      onChange={(e) => handleChange(e.target.name, e.target.value)}
                      value={userData.password}
                      helperText={errors.password !== '' ? errors.password : ''}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      error={errors.password_confirmation !== ''}
                      margin='normal'
                      required
                      fullWidth
                      name='password_confirmation'
                      label='Confirm Password'
                      type='password'
                      id='password'
                      onChange={(e) => handleChange(e.target.name, e.target.value)}
                      value={userData.password_confirmation}
                      helperText={errors.password_confirmation !== '' ? errors.password_confirmation : ''}
                    />
                  </Grid>
                </Grid>
                <Button
                  fullWidth
                  name='next'
                  variant='contained'
                  sx={{ mt: 3, ml: 1 }}
                  onClick={handleNext}
                >
              Update
                </Button>
              </Collapse>
            </>

          </Paper>
          <Copyright />
        </Container>
      );
    }
    return null;
  };


  return (
    <>
      <Favicon url={icon}></Favicon>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppBar
          position='absolute'
          color='default'
          elevation={0}
          sx={{
            position: 'relative',
            borderBottom: (t) => `1px solid ${t.palette.divider}`,
          }}
        >
          <Toolbar>
            <Typography variant='h6' color='inherit' noWrap>
              <img src={logo} style={{ width: '50%', marginTop: '9px' }} alt='logo' />
            </Typography>
          </Toolbar>
        </AppBar>
        {getComponent()}
      </ThemeProvider>
    </>
  );
}
